<script setup>
/* eslint-disable vue/no-v-html */
import { inject, ref, onMounted, computed, onBeforeMount } from 'vue';
import VehicleCard from '../components/VehicleCard.vue';
import PageSpinner from '../components/PageSpinner.vue';
import InformationModal from '../components/InformationModal.vue';
import InformationIcon from '../components/InformationIcon.vue';
import {
  addTrackingEvent,
  getSalesmanOfferings,
  getActiveOffersSummary,
} from '../services/digitalSalesService.js';
import { useMainStore } from '../store/index.js';
import { formatIsoStringToLocalDateTime } from '../utils/timeFormatting.js';

const adobeEvent = inject('adobeEvent');
const store = useMainStore();

onBeforeMount(() => {
  store.activePage = 'ACTIVE_OFFERS';
  store.showYearEndOfferBanner = false;
});

const registerVatPageUrl = process.env.VUE_APP_DS_REGISTER_VAT_URL;

const isLoading = ref(false);
const showError = ref(false);
const availableSalesmanOfferings = ref([]);
const selectedCompany = ref(null);
const selectedCompanyVatNip = ref(null);
const selectedOffering = ref(null);
const selectedOfferingId = ref(null);
const isCompanySelectionLoading = ref(false);
const isOfferingSelectionLoading = ref(false);
const showYearEndOfferInline = ref(false);
const showYear2024OfferInline = ref(false);
const showEcolutionBlob = ref(false);
const showDW5Background = ref(false);

const availableCategories = ref([]);
const availableFilters = ref([]);
const selectedFilters = ref({});
const availableOffers = ref({});


const toggleFilter = (filter, header) => {
  if (selectedFilters.value[header].includes(filter)) {
    selectedFilters.value[header] = selectedFilters.value[header].filter((f) => f !== filter);
  } else {
    selectedFilters.value[header].push(filter);
  }
  adobeEvent({
    event: 'toggleFilter',
    filterInfo: {
      filterName: filter,
      filterAction: selectedFilters.value[header].includes(filter) ? 'add' : 'remove',
    },
  });
};

const toggleCategory = (e) => {
  availableCategories.value = availableCategories.value.map(category => {
    if (category.id === e.target.id) category.checked = e.target.checked;
    return category;
  });
};

const sendTracking = () => {
  adobeEvent({
    event: 'pageView',
    page: {
      pageName: 'AvailableOffersView',
    },
  });
  if (store.tid) {
    const data = {
      event: 'availableOffersView',
      data: { timestamp: new Date().toISOString() },
    };
    addTrackingEvent(data, store.tid)
      .then(() => {
        store.tid = null;
      })
      .catch((err) => {
        console.error('failed to create tracking event', err);
      });
  }
};

const fetchOfferConfigurationImages = async () => {
  let offers = {};
  try {
    if (store.isSalesman) {
      offers = (await getActiveOffersSummary(selectedOffering.value?.offeringId)).data;
    } else {
      offers = (await getActiveOffersSummary()).data;
    }
  } catch (error) {
    if (error.response?.status === 404) {
      console.warn('No offers returned from backend');
    }
    else {
      console.error('Failed getActiveOffersSummary', error);
      throw error;
    }
  }

  let frameAgreementAvailable = false;
  const filterMap = new Map();

  Object.entries(offers).forEach(([specTypeKey, innerOffers]) => {

    const transformedOffers = innerOffers.map(
      ({
        offerId,  
        misc, 
        expired, 
        finalized, 
        isOfferNotValid = false, 
        createdDate, 
        sortOrder, 
        imageSpecificationAlt,
        filters,
        initialPrice,
        serviceSavings,
      }) => {
        const getOfferValidDate = () => {
          if (!misc?.offerValidUntilDate) return [];
          const date = new Date(misc.offerValidUntilDate);
          return [`${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getFullYear()).substring(2)}`];
        };

        let offerCategory;
        filters?.forEach(({ header, category, tags }) => {
          if (category) offerCategory = category;
          if (!filterMap.has(header)) filterMap.set(header, []);
          filterMap.get(header).push(...tags);
        });

        const checkIfStockSpec = () => misc?.specType && misc.specType === 'stock_vehicle';

        // Flag that a frame agreement is available among the offers
        if (misc?.frameAgreement) {
          frameAgreementAvailable = true;
        }

        const keyPoints = [
          misc?.vehicleCardHighlightsText1,
          misc?.vehicleCardHighlightsText2,
          misc?.vehicleCardHighlightsText3,
          misc?.vehicleCardHighlightsText4,
          misc?.vehicleCardHighlightsText5,
        ].filter(Boolean);

        return {
          offerId,
          createdDate,
          sortOrder,
          title: misc?.vehicleCardHighlightsHeader,
          title2: misc?.vehicleCardHighlightsHeader2,
          imageUrl: 'https://cdn.digitaldesign.scania.com/icons/website_hosted/truck.svg',
          additionalInfo: misc?.vehicleCardAdditionalInfo || null,
          specialOfferBannerHeader: misc?.vehicleCardSpecialOfferHeader,
          specialOfferBannerDescription: misc?.vehicleCardSpecialOfferDescription,
          keyPoints: keyPoints.map((text) => ({
            icon: 'tick',
            translationKey: text,
          })),
          offerStatus: { expired, finalized, isOfferNotValid },
          offerData: { offerValidDate: getOfferValidDate() },
          misc,
          imageSpecificationAlt: imageSpecificationAlt?.activeOffersPage,
          filters: filters?.map(({ header, tags }) => ({
            header,
            tags: tags.map((t) => t.label),
          })),
          initialPrice,
          category: offerCategory,
          serviceSavings,
          stockVehicle: !!checkIfStockSpec(),
          frameAgreement: misc?.frameAgreement,
          tailorMade: !checkIfStockSpec(),
        };
      },
    );
    availableOffers.value[specTypeKey] = transformedOffers;
  });

  const sortedAvailableOffers = Object.keys(availableOffers.value).sort().reduce((acc, key) => { acc[key] = availableOffers.value[key]; return acc; }, {});
  const offerCategories = Object.entries(sortedAvailableOffers).flatMap(([specTypeKey, offersArray]) => offersArray.filter(o => o.category).map(o => o.category));
  availableCategories.value = [...new Set(offerCategories)].map((category, i) => ({ id: `category-${i + 1}`, title: category, text: `${category}Text`, checked: true }));
  if (!availableCategories.value.length) availableCategories.value = [{ id: 'no-category', checked: true }];

  store.frameAgreementAvailable = frameAgreementAvailable;

  availableFilters.value = Array.from(filterMap.entries()).map(
      ([header, tagObjects]) => {
      const tagsWithGroup = tagObjects.filter((tagObj) => tagObj.group);
      const sortedTags = tagsWithGroup.sort((a, b) => {
        const groupCompare = a.group.localeCompare(b.group);
        return groupCompare !== 0 ? groupCompare : a.label.localeCompare(b.label);
      });
      const labelsOnly = sortedTags.map((t) => t.label);
      const uniqueLabels = [...new Set(labelsOnly)];

      return {
        header,
        tags: uniqueLabels,
      };
    },
  );
  selectedFilters.value = Object.fromEntries(
    availableFilters.value.map(({ header }) => [header, []]),
  );
};

const fetchSalesmanOfferings = async () => {
  try {
    const response = await getSalesmanOfferings();

    availableSalesmanOfferings.value = Object.values(response.data).map(company => {
      return {
        ...company,
        offerings: company.offerings.map(offering => ({
          ...offering,
          createdDate: formatIsoStringToLocalDateTime(offering.createdDate, store.locale),
          offerValidUntilDate: formatIsoStringToLocalDateTime(offering.offerValidUntilDate, store.locale),
        })),
      };
    });
    selectedCompany.value = availableSalesmanOfferings.value[0];
    selectedCompanyVatNip.value = selectedCompany.value?.vatNip;
    selectedOffering.value = selectedCompany.value.offerings[0];
    selectedOfferingId.value = selectedOffering.value.offeringId;
  } catch (err) {
    console.error('error fetching salesman offerings: ', err);
    showError.value = true;
  }
};


const setFeatureToggle = async () => {
  store.showYearEndOfferBanner = false;
  showDW5Background.value = false;
  if (Object.values(availableOffers.value).some(array => array.length > 0)) {
    const hasFrameAgreement = Object.values(availableOffers.value).some((aO) => aO.frameAgreement);
    const hasStockVehicle = Object.values(availableOffers.value).some((aO) => aO.stockVehicle && !aO.frameAgreement);
    const hasTailorMade = Object.values(availableOffers.value).some((aO) => aO.tailorMade && !aO.frameAgreement);
    const hasOnlyStockVehicle = hasStockVehicle && !hasFrameAgreement && !hasTailorMade;

    if (hasStockVehicle) {
      const yearEndToggle = store.featureToggleData[`${store.market}#webapp_yearEndOfferBanner`];
      if (yearEndToggle) {
        const { isActive, isEffectiveUser, isFeatureDateValid } = yearEndToggle;
        const showYearEndOffer = isActive && isEffectiveUser && isFeatureDateValid;
        if (hasOnlyStockVehicle) store.showYearEndOfferBanner = showYearEndOffer;
        else showYearEndOfferInline.value = showYearEndOffer;
      }
    }

    const ecolutionToggle = store.featureToggleData[`${store.market}#webapp_show_ecolutionBlob`];
    if (ecolutionToggle) showEcolutionBlob.value = ecolutionToggle.isActive;
  }
  const response = store.featureToggleData[`${store.market}#webapp_show_availableOffersPage_DW5_background`];
  showDW5Background.value = response.isActive;
};


const applySelectedFilters = (offer) => {
  for (const [header, selectedTags] of Object.entries(selectedFilters.value)) {
    if (selectedTags.length === 0) continue;
    const filterObj = offer.filters?.find((f) => f.header === header);
    if (!filterObj || selectedTags.some((tag) => !filterObj.tags.includes(tag))) {
      return false;
    }
  }
  return true;
};


const hasAnyOffers = computed(() => Object.values(availableOffers.value).some(array => array.length > 0));
const hasAnyFilteredOffers = computed(() => dynamicOffersGroupsCategories.value.some((category) => category.offersGroups.some((group) => group.offers.length > 0)));

const dynamicOffersGroupsCategories = computed(() => {
  const sortedAvailableOffers = Object.keys(availableOffers.value).sort().reduce((acc, key) => { acc[key] = availableOffers.value[key]; return acc; }, {});
  return availableCategories.value.filter(({ checked }) => checked).map(({ id, title, text }) => {
    const offersGroups = Object.entries(sortedAvailableOffers).map(([specTypeKey, offersArray], index) => {
      const filteredOffers = offersArray.filter((offer) => applySelectedFilters(offer)).filter((offer) => ((offer.category === title) || !title));
      return {
        id: index + 1,
        title: specTypeKey,
        text: `${specTypeKey}Text`,
        offers: filteredOffers,
        cardHeaderChips: { specialOffer: showYearEndOfferInline.value || showYear2024OfferInline.value, ecolution: showEcolutionBlob.value }, // if apply Emil wants to have for all type vehicles,
      };
    });
    return { id, title, text, offersGroups };
  });
});

const handleCompanySelection = async (companyVatNip) => {
  isCompanySelectionLoading.value = true;
  selectedCompanyVatNip.value = companyVatNip;
  selectedCompany.value = availableSalesmanOfferings.value.find((company) => company.vatNip === companyVatNip);
  selectedOffering.value = selectedCompany.value.offerings[0];
  selectedOfferingId.value = selectedOffering.value.offeringId;
  await handleOfferingSelection(selectedOfferingId.value);
  isCompanySelectionLoading.value = false;
};

const handleOfferingSelection = async (offeringId) => {
  isOfferingSelectionLoading.value = true;
  selectedOffering.value = selectedCompany.value.offerings.find(offering => offering.offeringId === offeringId);
  selectedOfferingId.value = offeringId;
  await fetchOfferConfigurationImages();
  isOfferingSelectionLoading.value = false;
};

const showFilters = computed(() => {
  return store.activeMarket?.showFiltersOnActiveOffersPage;
});

onMounted(async () => {
  isLoading.value = true;
  sendTracking();
  if (store.isSalesman) await fetchSalesmanOfferings();
  await fetchOfferConfigurationImages();
  await setFeatureToggle();
  isLoading.value = false;
});

</script>
<template>
  <template v-if="isLoading">
    <PageSpinner />
  </template>
  <template v-else>
    <div class="active-offers__container">
      <div
        class="sdds-u-relative"
        :class="showDW5Background ? `active-offers__header__${store.market}__DW5` : 'active-offers__header'"
      >
        <div class="active-offers__header-content">
          <div>
            <p class="sdds-paragraph-01">
              {{ $i18n('availableOffersHeroTitle') }}
            </p>
            <p
              class="sdds-expressive-headline-01"
              v-html="$i18n('availableOffersHeroHeader')"
            />
            <p
              class="sdds-paragraph-02"
              v-html="$i18n('availableOffersHeroBody')"
            />
          </div>
          <template v-if="showEcolutionBlob">
            <div class="ecolution-blob">
              <h2 class="sdds-u-m0">
                {{ $i18n('availableOffersOfferTitle') }}
              </h2>
              <p class="sdds-u-m0">
                {{ $i18n('availableOffersOfferDescription') }}
              </p>
            </div>
          </template>
        </div>
      </div>
      <template v-if="!hasAnyOffers">
        <div class="no-active-offers sdds-u-mt3">
          <h1>{{ $i18n('noActiveOffers') }}</h1>
          <p>{{ $i18n('noActiveOffersText') }}</p>
          <a
            class="sdds-btn sdds-btn-primary"
            :href="registerVatPageUrl"
          >
            {{ $i18n('renewOffersButton') }}
          </a>
        </div>
      </template>
      <div
        class="active-offers__main-content"
        :class="showEcolutionBlob ? 'is-there-blob' : ''"
      >
        <template v-if="showFilters">
          <h3
            v-if="availableFilters.length || availableCategories.length > 1"
            :class="`sdds-headline-02 sdds-u-mt1 sdds-u-mb3 filter-headline-${store.market}`"
          >
            {{ $i18n('filter') }}
          </h3>
          <div
            v-if="availableFilters.length"
            class="available-filters-container"
          >
            <template
              v-for="{ header, tags } in availableFilters"
              :key="header"
            >
              <div
                v-if="tags.length > 1 && Object.values(availableOffers).some(array => array.length > 1)"
                class="available-filter-group"
              >
                <sdds-dropdown
                  :placeholder="$i18n(header)"
                  type="multiselect"
                  style="--sdds-dropdown-bg: #F9FAFB;"
                  @:select-option="(e) => toggleFilter(e.target.value, header)"
                >
                  <sdds-dropdown-option
                    v-for="tag in tags"
                    :key="tag"
                    :value="tag"
                    class="sdds-dropdown-option-background"
                  >
                    {{ $i18n(tag) }}
                  </sdds-dropdown-option>
                </sdds-dropdown>
              </div>
            </template>
          </div>
          <div
            v-if="availableCategories.length > 1"
            class="available-categories-container"
          >
            <label class="sdds-form-label">
              {{ $i18n('leasingOptions') }}
            </label>
            <template
              v-for="{ id, title, checked } in availableCategories"
              :key="id"
            >
              <div class="sdds-checkbox-item">
                <input
                  :id="id"
                  type="checkbox"
                  class="sdds-form-input"
                  :checked="checked"
                  @change="toggleCategory"
                >
                <label
                  :for="id"
                  class="sdds-form-label"
                >
                  {{ $i18n(title) }}
                </label>
              </div>
            </template>
          </div>
        </template>
        <template v-if="store.isSalesman">
          <div>
            <p class="sdds-headline-01">
              {{ $i18n('availableSalesmanOffersBodyHeading') }}
            </p>
            <p class="sdds-paragraph-02">
              {{ $i18n('availableSalesmanOffersBodyText') }}
            </p>
          </div>
          <div v-if="availableSalesmanOfferings">
            <div class="salesman-offerings">
              <div class="sdds-row">
                <div class="sdds-col-xs-12 sdds-col-sm-12 sdds-col-md-12 sdds-col-lg-4 sdds-u-pl0">
                  <div class="sdds-col-12">
                    <label
                      for="companyDropdown"
                      class="sdds-headline-02"
                    >
                      {{ $i18n('availableSalesmanOffersTitleCustomer') }}
                    </label>
                    <sdds-dropdown
                      style="--sdds-dropdown-bg: #F9FAFB;"
                      @:select-option="(e) => handleCompanySelection(e.target.value)"
                    >
                      <sdds-dropdown-option
                        v-for="({ vatNip, companyName }) in availableSalesmanOfferings"
                        :key="vatNip"
                        :value="vatNip"
                        class="sdds-dropdown-option-background"
                      >
                        {{ companyName }}
                      </sdds-dropdown-option>
                    </sdds-dropdown>
                  </div>
                  <div
                    v-if="selectedCompany?.offerings"
                    class="sdds-col-12 sdds-u-mt3"
                  >
                    <label
                      for="offeringDropdown"
                      class="sdds-headline-02"
                    >
                      {{ $i18n('Offerings (needs translation key)') }}
                    </label>
                    <sdds-dropdown
                      style="--sdds-dropdown-bg: #F9FAFB;"
                      @:select-option="(e) => handleOfferingSelection(e.target.value)"
                    >
                      <sdds-dropdown-option
                        v-for="({ offeringId, createdDate }) in selectedCompany.offerings"
                        :key="offeringId"
                        :value="offeringId"
                        class="sdds-dropdown-option-background"
                      >
                        {{ createdDate }}
                      </sdds-dropdown-option>
                    </sdds-dropdown>
                  </div>
                </div>
                <div
                  v-if="selectedOffering"
                  class="sdds-col-xs-12 sdds-col-sm-12 sdds-col-md-12 sdds-col-lg-8 sdds-u-pr0"
                >
                  <div class="company-information">
                    <div class="sdds-row">
                      <div class="sdds-col-xs-12 sdds-col-sm-4 sdds-headline-04">
                        {{ $i18n('availableSalesmanOffersTitleCompany') }}
                      </div>
                      <div class="sdds-col-xs-12 sdds-col-sm-8 sdds-paragraph-02">
                        {{ selectedCompany.companyName }}
                      </div>
                    </div>
                    <div class="sdds-row sdds-u-pt2">
                      <div class="sdds-col-xs-12 sdds-col-sm-4 sdds-headline-04">
                        {{ $i18n('availableSalesmanOffersTitleContactName') }}
                      </div>
                      <div class="sdds-col-xs-12 sdds-col-sm-8 sdds-paragraph-02">
                        {{ selectedOffering.customerName }}
                      </div>
                    </div>
                    <div class="sdds-row sdds-u-pt2">
                      <div class="sdds-col-xs-12 sdds-col-sm-4 sdds-headline-04">
                        {{ $i18n('availableSalesmanOffersTitleContactEmail') }}
                      </div>
                      <div class="sdds-col-xs-12 sdds-col-sm-8 sdds-paragraph-02">
                        {{ selectedOffering.customerEmail }}
                      </div>
                    </div>
                    <div class="sdds-row sdds-u-pt2">
                      <div class="sdds-col-xs-12 sdds-col-sm-4 sdds-headline-04">
                        {{ $i18n('availableSalesmanOffersTitleVat') }}
                      </div>
                      <div class="sdds-col-xs-12 sdds-col-sm-8 sdds-paragraph-02">
                        {{ selectedCompany.vatNip }}
                      </div>
                    </div>
                    <div class="sdds-row sdds-u-pt2">
                      <div class="sdds-col-xs-12 sdds-col-sm-4 sdds-headline-04">
                        {{ $i18n('availableSalesmanOffersTitleClassification') }}
                      </div>
                      <div class="sdds-col-xs-12 sdds-col-sm-8 sdds-paragraph-02">
                        {{ selectedCompany.customerClassification }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
        <template v-if="isOfferingSelectionLoading">
          <div class="spinner fa-4x">
            <font-awesome-icon
              icon="spinner"
              class="fa-spin"
            />
          </div>
        </template>
        <template v-else>
          <template v-if="hasAnyFilteredOffers">
            <template
              v-for="category in dynamicOffersGroupsCategories"
              :key="category.id"
            >
              <div
                v-if="category.id !== 'no-category' && category.offersGroups.some((group) => group.offers.length > 0)"
                class="category-container"
              >
                <p class="sdds-headline-01">
                  {{ $i18n(category.title) }}
                </p>
                <div :id="`modal-${category.id}`">
                  <a
                    href="#"
                    @click.prevent
                  >{{ $i18n('moreInformationText') }}</a>
                  <!-- <InformationIcon /> -->
                </div>
              </div>
              <template
                v-for="group in category.offersGroups"
                :key="group.id"
              >
                <div v-if="group.offers.length">
                  <div>
                    <p :class="`sdds-headline-02 sdds-u-mb0 group-title-${store.market}`">
                      {{ $i18n(group.title) }}
                    </p>
                    <p class="sdds-paragraph-03 sdds-u-mb0">
                      {{ $i18n(group.text) }}
                    </p>
                  </div>
                  <!-- Hiding below service savings banner. To Do: If not needed later, then below banner code block can be removed -->
                  <div
                    v-if="false && group.serviceSavingsOffers"
                    class="service-savings-banner sdds-banner sdds-banner-info el-margin-top-16"
                  >
                    <div class="sdds-banner-body sdds-u-pr0">
                      <div class="sdds-banner-subheader service-savings-banner-text">
                        {{ $i18n('serviceSavingsAvailable') }}
                      </div>
                      <div
                        :id="`modal-${category.id}-${group.title}`"
                        :data-analytics="`{'eventComponent':'PublicAvailableOffersView','eventType':'modal','eventLabel':'Service savings information'}`"
                        :data-dd-action-name="`Service savings information`"
                        style="cursor: pointer;"
                        class="sdds-u-pl1"
                      >
                        <!-- <a
                          href="#"
                          data-dd-action-name="more information"
                          @click.prevent
                        >{{ $i18n('moreInformationText') }}</a> -->
                        <InformationIcon
                          view-box="0 0 30 30"
                          :view-box-style="{ width: '5rem', color: '#0B1320' }"
                        />
                      </div>
                    </div>
                    <InformationModal
                      :id="`modal-${category.id}-${group.title}`"
                      :information-text="$i18n('serviceSavingsInformation')"
                    />
                  </div>
                  <div class="active-offers__card-list sdds-u-mt0 sdds-u-mb3">
                    <VehicleCard
                      v-for="card in group.offers"
                      :key="card.offerId"
                      :offer-id="card.offerId"
                      :service-savings="card.serviceSavings || []"
                      :title="card.title"
                      :title2="card.title2"
                      :keypoints="card.keyPoints"
                      :vehicle-card-image-key="card.vehicleCardImageKey"
                      :image-url="card.imageUrl"
                      :alt-image="card.imageSpecificationAlt"
                      :offer-status="card.offerStatus"
                      :offer-data="card.offerData"
                      :card-header-chips="group.cardHeaderChips"
                      :card-chips="card.misc.cardChips || {}"
                      :card-blobs="card.misc.cardBlobs || {}"
                      :card-background="card.misc.cardBackground"
                      :additional-info="card.additionalInfo"
                      :special-offer-banner-header="card.specialOfferBannerHeader"
                      :special-offer-banner-description="card.specialOfferBannerDescription"
                      :initial-price="(card.misc.vehicleCardShowPrice && card.initialPrice?.price) ? card.initialPrice : null"
                      :offer-category="category.title"
                    />
                  </div>
                </div>
              </template>
              <InformationModal
                :id="`modal-${category.id}`"
                :title="$i18n(category.title)"
                :information-text="$i18n(category.text)"
              />
            </template>
          </template>
          <template v-else>
            <div class="no-filtered-offers">
              <h1>{{ $i18n('noFilteredOffers') }}</h1>
              <p>{{ $i18n('noFilteredOffersText') }}</p>
            </div>
          </template>
        </template>
      </div>
    </div>
  </template>
</template>

<style scoped>
.sdds-expressive-headline-01 {
  margin: unset;
  line-height: 1.2;
}
.active-offers__header > div > .sdds-paragraph-01 {
  margin: 0;
  padding: 0;
}
.active-offers__card-list {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--sdds-spacing-element-32);
  width: 100%;
  margin-top: var(--sdds-spacing-element-16);
}
.active-offers__card-list > * {
  width: auto;
  margin: 0;
}
.active-offers__container {
  display: flex;
  flex-direction: column;
}
.active-offers__header {
  background-image: url('@/assets/active-offers-bg.jpeg');
  background-repeat: no-repeat;
  background-position: 60% 60%;
  background-size: cover;
  min-height: 40vh;
  color: white;
}
.active-offers__header > div {
  display: flex;
  flex-direction: column;
  width: 60%;
  margin: var(--sdds-spacing-element-24) var(--sdds-spacing-element-48);
}
.ecolution-blob {
  position: absolute;
  display: inline-block;
  right: 150px;
  bottom: -130px;
  width: 228px !important;
  height: 228px !important;
  border-radius: 50%;
  border: 2px solid #C84A07F2;
  background: linear-gradient(0deg, rgba(200, 74, 7, 0.95), rgba(200, 74, 7, 0.95)), linear-gradient(0deg, rgba(217, 86, 16, 0.95), rgba(217, 86, 16, 0.95));
  text-align: center;
  padding-top: 65px;
}
.ecolution-blob p {
  line-height: 1.3em;
}
.active-offers__header__pl__DW5 {
  background-image: url('@/assets/NEWHeroCutOrginalMedium - compact.jpg');
  background-repeat: no-repeat;
  background-position: unset;
  background-size: cover;
  min-height: 40vh;
  color: white;
}
.active-offers__header__pl__DW5 .sdds-paragraph-02 {
  max-width: 55%;
}
.active-offers__header__de__DW5 {
  /* background-image: url('@/assets/super-11-hero.jpeg'); */
  background-image: url('@/assets/Hero_image_DS_Germany_20260529.png');
  background-repeat: no-repeat;
  /* background-position: unset; */
  background-position: center;
  background-size: cover;
  min-height: 50vh;
  color: white;
}
.active-offers__header__de__DW5 .sdds-paragraph-02 {
  max-width: 55%;
}
.active-offers__header-content {
  display: flex;
  flex-direction: column;
  min-height: 36vh;
  justify-content: space-between;
  margin: var(--sdds-spacing-element-4) var(--sdds-spacing-element-48);
}
.active-offers__main-content {
  display: flex;
  flex-direction: column;
  margin: var(--sdds-spacing-element-24) var(--sdds-spacing-element-48);
}
.filter-headline-ch {
  font-size: 20px !important;
  font-weight: normal !important;
  margin-bottom: var(--sdds-spacing-layout-16) !important;
}
.salesman-offerings {
  background: var(--sdds-grey-100);
  margin: 0px -48px;
  padding: 24px 48px 48px;
}
.company-information {
  padding-top: 44px;
}
.spinner {
  margin-top: 16px;
  height: inherit;
}
.no-active-offers, .no-filtered-offers {
  display: flex;
  flex-flow: column;
  align-items: center;
}
.available-filters-container, .available-categories-container {
  display: flex !important;
  flex-direction: row !important;
  gap: var(--sdds-spacing-element-16);
  align-items: center;
}
.group-title-ch {
  font-size: 48px !important;
}
.available-filter-group {
  display: inline-flex;
}
.available-filter-group .sdds-dropdown-multiselect {
  width: 300px;
}
.available-filter-group .sdds-dropdown-multiselect .sdds-dropdown-option-background {
  background-color: #F9FAFB;
}
.category-container {
  display: flex;
  gap: 1em;
  align-items: baseline;
  margin: 3em 0 1em;
}
.category-container .sdds-headline-01 {
  margin: 0;
  font-family: "Scania Sans", arial, helvetica, sans-serif;
}
.sdds-checkbox-item {
  padding-right: var(--sdds-spacing-element-16);
}
.sdds-checkbox-item:last-of-type {
  padding-right: 0;
}

.service-savings-banner {
  background-color: #9BD9C2;
  padding: 8px 16px;
  border-radius: 4px;
  margin-bottom: var(--sdds-spacing-element-16);
}
.service-savings-banner .sdds-banner-body {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.service-savings-banner-text {
  color: #0B1320;
  font-size: 16px;
  font-weight: bold;
}

@media only screen and (max-width: 1366px) {
  .active-offers__card-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .group-title-ch {
    font-size: 40px !important;
  }
}
@media only screen and (max-width: 1294px) {
  .ecolution-blob {
    right: 0px;
  }
  .is-there-blob {
    margin-top: var(--sdds-spacing-layout-72) !important;
  }
}
@media only screen and (max-width: 1150px) {
  .active-offers__header__pl__DW5, .active-offers__header__de__DW5 {
    background-position: 90%;
  }
}
@media only screen and (max-width: 1024px) {
  .sdds-expressive-headline-01 {
    font-size: 4em;
  }
  .active-offers__card-list > * {
    margin: unset;
  }
}
@media only screen and (max-width: 768px) {
  .group-title-ch {
    font-size: 32px !important;
  }
  .active-offers__header__pl__DW5, .active-offers__header__de__DW5 {
    min-height: unset;
  }
  .sdds-expressive-headline-01 {
    font-size: 3em;
  }
  .sdds-paragraph-02 {
    font-size: 16px;
  }
  .active-offers__header__pl__DW5 .sdds-paragraph-02 {
    max-width: unset;
  }
  .category-container {
    margin-top: 2em;
  }
  .category-container .sdds-headline-01 {
    font-size: 2em;
  }
  .sdds-headline-02 {
    font-size: 1.8em;
    margin-top: 2rem;
  }
  .active-offers__header > div {
    width: 80%;
    margin: var(--sdds-spacing-element-24) var(--sdds-spacing-element-16);
  }
  .active-offers__main-content {
    margin: var(--sdds-spacing-element-24) var(--sdds-spacing-element-16);
  }
  .active-offers__card-list {
    grid-template-columns: 1fr;
  }

  .sdds-paragraph-01 {
    font-size: 5rem;
  }
  .salesman-offerings {
    margin: 0px 0px;
  }
  .ecolution-blob {
    width: 160px !important;
    height: 160px !important;
    bottom: -100px;
    padding-top: 2.7em;
  }
  .ecolution-blob h2 {
    font-size: 1.9em;
  }
  .ecolution-blob p {
    font-size: 0.9em;
  }
}
@media only screen and (max-width: 660px) {
  .available-filters-container, .available-categories-container {
    flex-direction: column !important;
    gap: var(--sdds-spacing-element-16);
    align-items: unset;
  }
  .filter-headline-ch {
    font-size: 16px !important;
    margin-bottom: var(--sdds-spacing-layout-8) !important;
  }
  .available-categories-container {
    padding-top: var(--sdds-spacing-element-16);
  }
  .sdds-checkbox-item {
    padding-right: 0;
  }
  .category-container {
    margin-top: 1.5em;
  }
  .category-container .sdds-headline-01 {
    font-size: 1.8em;
  }
  .sdds-headline-02 {
    font-size: 1.5em;
    margin-top: 1.5rem;
  }
  .sdds-expressive-headline-01 {
    font-size: 2.5em;
  }
}
@media only screen and (max-width: 650px) {
  .ecolution-blob {
    bottom: -140px;
  }
}
</style>
